// ============================================
// @narbodica/auth — utils/passkey.js
// ============================================
// WebAuthn passkey utilities using @simplewebauthn/browser.
//
// All functions that hit the backend take `apiBase` as a required
// first parameter. Callers must pass the URL — there is no hardcoded
// fallback. Each app supplies the URL from its own canonical source
// (src/utils/apiBase.js or src/apiConfig.js).
//
// Updated 2 May 2026 (Cassiopeia stretch 13) — removed hardcoded URL
// fallback to maintain single source of truth across the suite. The
// hardcoded URL previously here was the same as each app's apiBase.js,
// creating drift risk. Now centralized at the app level.
// ============================================

import { startAuthentication, startRegistration } from '@simplewebauthn/browser';

function requireApiBase(apiBase) {
  if (!apiBase) {
    throw new Error('passkey.js: apiBase parameter is required (no hardcoded fallback)');
  }
}

export async function checkPasskeySupport() {
  if (window.Capacitor?.isNativePlatform?.()) return false;
  try {
    if (typeof window === "undefined" || !window.PublicKeyCredential) return false;
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Login with passkey — uses the direct /api/passkey/auth/options route
 * and @simplewebauthn/browser to handle encoding correctly.
 */
export async function loginWithPasskey(apiBase, email) {
  requireApiBase(apiBase);
  try {
    // Get challenge from server
    const optionsRes = await fetch(`${apiBase}/api/passkey/auth/options`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(email ? { email } : {}),
    });

    if (!optionsRes.ok) {
      const err = await optionsRes.json();
      return { success: false, error: err.message, noPasskeys: err.noPasskeys };
    }

    const optionsData = await optionsRes.json();
    if (!optionsData.success) {
      return { success: false, error: optionsData.message, noPasskeys: optionsData.noPasskeys };
    }

    // Use SimpleWebAuthn to handle the browser prompt — it handles all encoding
    let credential;
    try {
      credential = await startAuthentication({ optionsJSON: optionsData.data });
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        return { success: false, error: 'Authentication cancelled or timed out' };
      }
      if (err.name === 'InvalidStateError') {
        return { success: false, noPasskeys: true, error: 'No passkey found for this device' };
      }
      return { success: false, error: 'Authentication failed. Please try your password.' };
    }

    // Verify with server
    const verifyRes = await fetch(`${apiBase}/api/passkey/auth/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential, email }),
    });

    const data = await verifyRes.json();
    if (!verifyRes.ok || !data.success) {
      return { success: false, error: data.message || 'Verification failed' };
    }

    return {
      success: true,
      tokens: data.tokens || data.data?.tokens,
      user: data.user || data.data?.user,
    };
  } catch (err) {
    return { success: false, error: 'Authentication failed. Please try your password.' };
  }
}

/**
 * Register a passkey — delegates to the local utils/passkey.js via apiBase.
 */
export async function registerPasskey(apiBase, token) {
  requireApiBase(apiBase);
  try {
    const challengeRes = await fetch(`${apiBase}/api/passkey/register/options`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    if (!challengeRes.ok) throw new Error('Could not start passkey registration');

    const optionsData = await challengeRes.json();

    let credential;
    try {
      credential = await startRegistration({ optionsJSON: optionsData.data });
    } catch (err) {
      if (err.name === 'NotAllowedError') return { success: false, error: 'Registration cancelled' };
      return { success: false, error: 'Passkey registration failed: ' + err.message };
    }

    const verifyRes = await fetch(`${apiBase}/api/passkey/register/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ credential }),
    });

    const data = await verifyRes.json();
    return { success: data.success, error: data.message };
  } catch (err) {
    return { success: false, error: 'Passkey registration failed. Please try again.' };
  }
}
